/**
 * Firm branding on the client-facing form: turns the config a firm saved in the
 * console into the logo, colors and copy of the welcome screen.
 *
 * The shape and its defaults come from `@formio/convex-api` — the same module
 * the schema and the console editor use, so the three can't drift.
 */

import type { CSSProperties } from "react";
import {
  brandingFromStored,
  DEFAULT_BACKGROUND,
  DEFAULT_BRANDING,
  DEFAULT_CARD,
  ensureReadable,
  foregroundOn,
  foregroundOnHsl,
  GRAPHIC_CONTRAST,
  hexToHsl,
  hexToHslTriplet,
  hslCss,
  hslTriplet,
  parseHex,
  renderTemplate,
  surfaceTokens,
  TEXT_CONTRAST,
  type BrandingConfig,
  type TemplateVars,
} from "@formio/convex-api";
import type { UiTranslations } from "@/lib/ui-translations";

export { DEFAULT_BRANDING, renderTemplate };
export type { BrandingConfig, TemplateVars };

export interface ResolvedBranding {
  /**
   * Every token, ready to be written on `<html>` — which is what makes the whole
   * journey branded and not just the welcome screen: the wizard, the completion
   * screen and the dropdowns that portal out to `<body>` all inherit from there.
   *
   * Ink tokens in here are raised against the PAGE background. The card-scoped
   * versions ship as `--brand-card-*` and index.css remaps them on `.bg-card`.
   * Empty without a config, so the stock theme is left completely alone.
   */
  rootVars: Record<string, string>;
  /** Whether to stamp `data-brand-card` on `<html>`, which arms that remap. */
  brandsCards: boolean;
  logoUrl: string | null;
  /** Greeting headline, `{prenom}` already replaced. */
  greeting: string;
  /** Intro paragraph, `{cabinet}` already replaced. */
  message: string;
  showChecklist: boolean;
  /**
   * For the brand-colored bits sitting on the page background (the wordmark, the
   * "Formio" in the footer, the support link). `--primary` stays the raw brand
   * color because buttons fill with it, so text using it has to be corrected
   * where it sits — and `text-primary` beats an inherited color anyway.
   */
  primaryOnBackgroundStyle: CSSProperties;
  /** Raw page background, for painting `body` so overscroll matches. */
  backgroundColor: string | null;
}

// A firm that never edited the copy keeps the default French strings; using them
// verbatim would hand a French greeting to an English or Spanish client. Only
// text the firm actually changed overrides the translated copy.
function isCustomCopy(value: string, fallback: string): boolean {
  const trimmed = (value ?? "").trim();
  return trimmed !== "" && trimmed !== fallback;
}

/**
 * Every token the firm's colors imply, as `H S% L%` triplets keyed by CSS custom
 * property. Written on `<html>`, so the entire form inherits them — including
 * the dropdowns, dialogs and toasts that render outside the React tree.
 *
 * Two families come out of here:
 *  - the page-level tokens, whose ink is raised against the PAGE background;
 *  - `--brand-card-*`, the same ink raised against the CARD, which index.css
 *    remaps onto `.bg-card` so a card and the page behind it can disagree.
 */
function buildRootVars(config: BrandingConfig): Record<string, string> {
  const vars: Record<string, string> = {};

  // The brand color as a *fill* — buttons, the progress bar. A firm that picks a
  // primary close to its own background paints a button that isn't there, so the
  // fill is raised to 3:1 against whatever it sits on. Twice, because a card and
  // the page behind it are two different things to sit on: the root value is
  // measured against the page, `--brand-card-primary` against the card, and
  // index.css swaps them over on `.bg-card`.
  const primaryHsl = hexToHsl(config.primaryColor);

  const accent = hexToHslTriplet(config.accentColor);
  if (accent) {
    vars["--accent"] = accent;
    vars["--accent-foreground"] = hslTriplet(foregroundOn(config.accentColor));
  }

  // Page background and everything drawn straight onto it.
  const backgroundHex = parseHex(config.backgroundColor)
    ? config.backgroundColor
    : DEFAULT_BACKGROUND;
  const background = hexToHslTriplet(backgroundHex);
  if (background) vars["--background"] = background;
  const onBackground = surfaceTokens(backgroundHex);
  vars["--foreground"] = hslTriplet(onBackground.foreground);
  vars["--muted-foreground"] = hslTriplet(onBackground.mutedForeground);
  vars["--muted"] = hslTriplet(onBackground.muted);
  vars["--border"] = hslTriplet(onBackground.outline);
  vars["--input"] = hslTriplet(onBackground.outline);

  // The card, and the same four tokens computed against it. They stay inert
  // until index.css puts them to work on `.bg-card`.
  const cardHex = parseHex(config.cardColor) ? config.cardColor : DEFAULT_CARD;
  const card = hexToHslTriplet(cardHex);
  if (card) vars["--card"] = card;
  if (primaryHsl) {
    const onPage = ensureReadable(primaryHsl, backgroundHex, GRAPHIC_CONTRAST);
    vars["--primary"] = hslTriplet(onPage);
    vars["--primary-foreground"] = hslTriplet(foregroundOnHsl(onPage));
    vars["--ring"] = hslTriplet(onPage);

    const onCardPrimary = ensureReadable(primaryHsl, cardHex, GRAPHIC_CONTRAST);
    vars["--brand-card-primary"] = hslTriplet(onCardPrimary);
    vars["--brand-card-primary-foreground"] = hslTriplet(foregroundOnHsl(onCardPrimary));
  }

  const onCard = surfaceTokens(cardHex);
  vars["--brand-card-foreground"] = hslTriplet(onCard.foreground);
  vars["--brand-card-muted-foreground"] = hslTriplet(onCard.mutedForeground);
  vars["--brand-card-muted"] = hslTriplet(onCard.muted);
  vars["--brand-card-border"] = hslTriplet(onCard.outline);

  // The accent doubles as an icon color on cards (checklist, autosave), so it
  // has to survive the card behind it — a pale accent on a white card is
  // invisible otherwise. Icons are graphics: 3:1 rather than 4.5:1.
  const accentHsl = hexToHsl(config.accentColor);
  if (accentHsl) {
    vars["--accent-on-card"] = hslTriplet(ensureReadable(accentHsl, cardHex, GRAPHIC_CONTRAST));
  }

  return vars;
}

/**
 * Brand color as *text* on the page background. `--primary` stays the raw brand
 * because buttons fill with it, so anything writing with it out there has to be
 * corrected on the spot.
 */
function buildPrimaryOnBackground(config: BrandingConfig): CSSProperties {
  const background = parseHex(config.backgroundColor) ? config.backgroundColor : DEFAULT_BACKGROUND;
  const primaryHsl = hexToHsl(config.primaryColor);
  return primaryHsl
    ? { color: hslCss(ensureReadable(primaryHsl, background, TEXT_CONTRAST)) }
    : {};
}

/**
 * Turns a firm config into everything the form needs to wear its colors — the
 * welcome screen, the wizard behind it, and the completion screen. A null config
 * (no branding, add-on off, or branding not loaded yet) resolves to the stock
 * appearance, and emits no tokens at all so the shipped theme is untouched.
 */
export function resolveBranding(
  config: BrandingConfig | null | undefined,
  vars: TemplateVars,
  t: UiTranslations,
): ResolvedBranding {
  const defaults: ResolvedBranding = {
    rootVars: {},
    brandsCards: false,
    logoUrl: null,
    greeting: `${t.hello} ${vars.prenom}`,
    message: t.welcomeMessage(vars.cabinet),
    showChecklist: true,
    primaryOnBackgroundStyle: {},
    backgroundColor: null,
  };

  if (!config) return defaults;

  const { greeting, message, showChecklist } = config.presentation ?? DEFAULT_BRANDING.presentation;

  return {
    rootVars: buildRootVars(config),
    brandsCards: true,
    logoUrl: config.logoUrl || null,
    greeting: isCustomCopy(greeting, DEFAULT_BRANDING.presentation.greeting)
      ? renderTemplate(greeting, vars)
      : defaults.greeting,
    message: isCustomCopy(message, DEFAULT_BRANDING.presentation.message)
      ? renderTemplate(message, vars)
      : defaults.message,
    showChecklist: showChecklist !== false,
    primaryOnBackgroundStyle: buildPrimaryOnBackground(config),
    backgroundColor: parseHex(config.backgroundColor) ? config.backgroundColor : null,
  };
}

// Flashy sample config used to develop and eyeball the feature before the
// firm's real branding is stored anywhere. Logo is inlined so the mock needs no
// network or asset pipeline.
const MOCK_LOGO =
  "data:image/svg+xml;utf8," +
  encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" width="200" height="64" viewBox="0 0 200 64">' +
      '<rect width="64" height="64" rx="16" fill="#6D28D9"/>' +
      '<text x="32" y="42" font-family="Inter,sans-serif" font-size="26" font-weight="700" fill="#fff" text-anchor="middle">CT</text>' +
      '<text x="78" y="41" font-family="Inter,sans-serif" font-size="22" font-weight="600" fill="#6D28D9">Cabinet T.</text>' +
      "</svg>",
  );

export const MOCK_BRANDING: BrandingConfig = {
  primaryColor: "#6D28D9",
  accentColor: "#DB2777",
  backgroundColor: "#F5F3FF",
  cardColor: "#FFFFFF",
  logoUrl: MOCK_LOGO,
  logoFileName: "cabinet-tremblay.svg",
  presentation: {
    greeting: "Bienvenue {prenom} 👋",
    message:
      "{cabinet} vous accompagne dans votre demande d'immigration. Prenez le temps qu'il vous faut : vos réponses sont conservées.",
    showChecklist: true,
  },
  subdomain: "cabinet-tremblay",
};

/** What `firms.getFormBranding` hands back for a branded firm. */
export type StoredFormBranding = Omit<BrandingConfig, "logoUrl" | "logoFileName"> & {
  logoUrl: string | null;
  logoFileName: string | null;
};

/**
 * The one place the form decides which branding to render: the firm's saved
 * config, or null for the stock appearance. `?branding=mock` short-circuits to
 * MOCK_BRANDING in dev builds so the screen can be eyeballed without a firm.
 */
export function getBrandingConfig(
  stored: StoredFormBranding | null | undefined,
): BrandingConfig | null {
  if (import.meta.env.DEV && typeof window !== "undefined") {
    const requested = new URLSearchParams(window.location.search).get("branding");
    if (requested === "mock") return MOCK_BRANDING;
  }
  if (!stored) return null;
  return brandingFromStored(stored, stored.logoUrl ?? null);
}
